<template>
	<form @submit.prevent="save" autocomplete="off" class="annotation-creation-container">
		<input type="hidden" name="annotatable_id" id="annotatable_id" v-model="artifact.id" />
		<input type="hidden" name="annotatable_type" id="annotatable_type" v-model="artifact.type" />

		<div class="bg-light-noise modal-topbar pl-3 py-2">
			<div class="modal-topbar-content"><b>{{ __('translations.title.annotation') }}</b></div>
		</div>
		<div class="p-3" style="flex: 1; overflow: hidden;">
			<div style="display: flex; flex-direction: column; height: 100%">

				<div v-if="warning" class="alert alert-warning alert-dismissible fade show" role="alert">
					{{ __('translations.message.annotation_form_warning') }}
					<button type="button" class="close" data-dismiss="alert" aria-label="Close" @click="warning = false">
						<span aria-hidden="true">&times;</span>
					</button>
				</div>
				
				<div class="card card-accent-success mb-3">
					<div class="card-header px-3 py-2">
						<i class="far fa-list-alt fa-lg mr-2"></i>{{ __('translations.title.general_info') }}
					</div>
					<div class="card-body p-3">
						<div class="form-group mb-0">
							<textarea class="form-control form-control-sm" rows="3" name="transcription" id="transcription" v-model="annotation.transcription" :placeholder="__('translations.label.transcription')" />
						</div>
					</div>
				</div>
				<div class="card card-accent-dark" style="flex: 1 1 auto; overflow: hidden;">
					<div class="card-header px-3 py-2">
						<i class="fab fa-wikipedia-w fa-lg mr-2"></i>{{ __('translations.title.wikidata_tags') }}
					</div>
					<div class="card-body p-3" style="overflow: hidden;">
						<wikidata-search taggableType="annotation"
							v-on:selected-tag="onSelectedTag"
							v-on:deselected-tag="onDeselectedTag"
							:populateWith="annotation.tags"></wikidata-search>
					</div>
				</div>
			</div>
		</div>
		<div class="bg-light-noise modal-bottombar px-3 py-2">
			<div class="d-flex justify-content-between align-self-center">
				<button type="submit" class="btn btn-info" :disabled="isSaving">{{ __('translations.button.save') }}</button>
				<button type="button" class="btn btn-secondary" @click="cancel">{{ __('translations.button.cancel') }}</button>
			</div>
		</div>
	</form>
</template>

<script>
import {mapGetters} from 'vuex'
import WikidataSearch from '../../components/WikidataSearch'

export default {
	components: {
		'wikidata-search' : WikidataSearch,
	},
	props: {
		artifact: Object,
		populateWith: {
			type: Object,
			default: () => ({ empty: true })
		}
	},
	data() {
		return {
			selection: null,
			warning: false,
			annotation: {
				transcription: '',
				tags: [],
				box_x: null,
				box_y: null,
				box_w: null,
				box_h: null,
			},
			isSaving: false,
		}
	},
	created() {
		if (!this.populateWith.empty) {
			this.annotation = this.populateWith;
			
			// Keep track of the original annotation information
			this.cachedAnnotation = JSON.parse(JSON.stringify(this.annotation));
		}

		var tiledImage = this.$parent.viewer.world.getItemAt(0);
		var imageBounds = tiledImage.getBounds();

		if (this.populateWith.empty) {
			// Display an initial selection in the middle.
			var viewportBounds = this.$parent.viewer.viewport.getBounds();
			var intersection = viewportBounds.intersection(imageBounds);
			
			if (this.artifact.type == 'image') {
				var defaultRect = new OpenSeadragon.SelectionRect(intersection.x + intersection.width / 3, intersection.y + intersection.height / 3, intersection.width / 3, intersection.height / 3);
			} else {
				var defaultRect = new OpenSeadragon.SelectionRect(intersection.x + intersection.width * 0.1, intersection.y + intersection.height * 0.4, intersection.width * 0.8, intersection.height * 0.2);
			}
			
		} else {
			// If it's in editing mode, display the annotation's saved region.
			var defaultRect = new OpenSeadragon.SelectionRect(this.annotation.box_x * imageBounds.width / this.artifact.iiif_info.width, this.annotation.box_y * imageBounds.height / this.artifact.iiif_info.height, this.annotation.box_w * imageBounds.width / this.artifact.iiif_info.width, this.annotation.box_h * imageBounds.height / this.artifact.iiif_info.height);	
		}

		var self = this;
		
		if (this.$parent.viewer != null && this.selection == null) {
			this.selection = this.$parent.viewer.selection({
				allowRotation: false,
				restrictToImage: true,
				showSelectionControl: false,
				showConfirmDenyButtons: false,
				rect: defaultRect,
				onSelection: function(rect) {
					self.annotation.box_x = rect.x;
					self.annotation.box_y = rect.y;
					self.annotation.box_w = rect.width;
					self.annotation.box_h = rect.height;
				}
			});
		}

		if (this.selection != null) {
			this.selection.enable();
		}

		// In edit mode, bring the selected region/annotation in the center and zoom in.
		if (!this.populateWith.empty) {
			var boundingBox = {
				x: Math.max(this.annotation.box_x - this.annotation.box_w * 0.1, 0),
				y: Math.max(this.annotation.box_y - this.annotation.box_h * 0.1, 0),
				w: Math.min(this.annotation.box_w * 1.2, this.artifact.iiif_info.width),
				h: Math.min(this.annotation.box_h * 1.2, this.artifact.iiif_info.height),
			};
			
			var boundingRect = new OpenSeadragon.SelectionRect(boundingBox.x * imageBounds.width / this.artifact.iiif_info.width, boundingBox.y * imageBounds.height / this.artifact.iiif_info.height, boundingBox.w * imageBounds.width / this.artifact.iiif_info.width, boundingBox.h * imageBounds.height / this.artifact.iiif_info.height);
			
			this.$parent.viewer.viewport.fitBounds(boundingRect, true);
		}
	},
	methods: {
		save() {
			this.isSaving = true;
			
			if (this.getActiveTags().length > 0 || (this.annotation.transcription !== '' && this.annotation.transcription !== null)) {
				// This will trigger the 'onSelection:' callback.
				this.selection.confirm();
				
				axios.post('/annotation/save', {annotatable_id: this.artifact.id, annotatable_type: this.artifact.type, annotation: this.annotation}).then(response => {
					if (this.populateWith.empty) {
						this.artifact.annotations.unshift(response.data.lastInsertedAnnotation);
					} else {
						Object.assign(this.annotation, response.data.lastInsertedAnnotation);
					}
					
					//Update annotations count on the home page.
					this.$parent.updateAnnotations();
					
					// Go back to the annotations tab.
					this.backToAnnotations();
					this.$parent.viewer.viewport.goHome();
					
					this.warning = false;
				}).catch(error => {
					console.log(error);
					this.isSaving = false;
				});
			} else {
				this.isSaving = false;
				this.warning = true;
			}
		},
		getActiveTags() {
			return this.annotation.tags.filter(function(tag) {
				return tag.action !== 'DELETE';
			});
		},
		cancel() {
			// Return the annotation data back to its previous state if it was an edit mode.
			if(this.cachedAnnotation) {
				Object.assign(this.annotation, JSON.parse(JSON.stringify(this.cachedAnnotation)));	
			}
			
			this.backToAnnotations();
			this.$parent.viewer.viewport.goHome();
		},
		backToAnnotations() {
			this.$parent.activeTab = 'annotation';
			this.$parent.annotationFormFlag = false;
		},
		onSelectedTag(tag) {
			Vue.set(tag, 'action', 'ADD');
			this.annotation.tags.push(tag);
		},
		onDeselectedTag(tag) {
			Vue.set(tag, 'action', 'DELETE');
		}
	},
	beforeDestroy() {
		this.$parent.viewer.removeAllHandlers('selection');

		// Disable the selection instance.
		if(this.selection != null) {
			this.selection.disable();
		}
	}
}
</script>